// Pinterest Tag (conversion tracking). Mirrors Pinterest's snippet: a `pintrk`
// stub that queues calls until core.js loads, then `load`. The tag id comes from
// env (Pinterest Ads Manager → Conversions); without it nothing loads.
const SCRIPT_ID = 'pinterest-tag';

type Pintrk = ((...args: unknown[]) => void) & {
  queue?: unknown[][];
  version?: string;
};

declare global {
  interface Window {
    pintrk?: Pintrk;
  }
}

// Install the pintrk stub + core.js once. Passing the user's email as `em`
// enables Enhanced Match (Pinterest hashes it client-side). Page visits flow
// through `trackPinterestPageVisit` (driven by the router), not fired here.
function loadScript(tagId: string, email?: string) {
  if (typeof document === 'undefined' || window.pintrk || document.getElementById(SCRIPT_ID)) return;

  const pintrk: Pintrk = function (...args: unknown[]) {
    pintrk.queue!.push(args);
  };
  pintrk.queue = [];
  pintrk.version = '3.0';
  window.pintrk = pintrk;

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = 'https://s.pinimg.com/ct/core.js';
  document.head.appendChild(script);

  window.pintrk('load', tagId, email ? { em: email } : undefined);
}

/** Load the Pinterest tag. No-op without a tag id. */
export function loadPinterest(tagId: string | undefined, email?: string) {
  if (!tagId) return;
  loadScript(tagId, email);
}

/** Re-load with the user's email for Enhanced Match. No-op until the tag loaded. */
export function identifyPinterest(user: { email?: string }, tagId: string | undefined) {
  if (!tagId || !window.pintrk || !user.email) return;
  window.pintrk('load', tagId, { em: user.email });
}

/** Fire a Pinterest PageVisit for an SPA navigation. No-op until the tag loaded. */
export function trackPinterestPageVisit() {
  window.pintrk?.('page');
}

/** Signup conversion. No-op until the tag has loaded. */
export function trackPinterestSignup() {
  window.pintrk?.('track', 'signup');
}

/** Checkout conversion with value. No-op until the tag has loaded. */
export function trackPinterestCheckout(details: { value: number; currency: string; transactionId?: string }) {
  window.pintrk?.('track', 'checkout', {
    value: details.value,
    currency: details.currency,
    order_id: details.transactionId,
  });
}
